import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useGetPagesQuery, useDeletePageMutation, LandingPage } from '../../api/pagesApi';
import { useAppSelector } from '../../hooks/redux';
import { PagePreview } from './PagePreview';
import { HoverPreview } from './HoverPreview';
import { CustomDomainModal } from './CustomDomainModal';
import { AnalyticsModal } from './AnalyticsModal';
import OnboardingTrigger from '../onboarding/OnboardingTrigger';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  margin: 0;
  color: #1f2937;
  font-size: 28px;
  font-weight: 700;
`;

const Controls = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 1;
  justify-content: flex-end;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  width: 240px;

  &:focus {
    outline: none;
    border-color: #FF6B6B;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #FF6B6B;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }
`;

const CreateButton = styled.button`
  background: linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.4);
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: #4a5568;
  border: 1px solid #d1d5db;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f7fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const PagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
`;

const PageCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const PageTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
`;

const StatusBadge = styled.span<{ isActive: boolean }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.isActive ? '#10b981' : '#ef4444'};
  color: white;
`;

const PageMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #6b7280;
`;

const MetaLabel = styled.span`
  font-weight: 500;
`;

const MetaValue = styled.span`
  color: #1f2937;
`;

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LinkValue = styled.span`
  color: #1f2937;
  font-family: monospace;
  font-size: 12px;
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
  word-break: break-all;
  flex: 1;
`;

const CopyLinkButton = styled.button`
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  padding: 4px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #cbd5e0;
  }

  &:active {
    background: #a0aec0;
  }
`;

const TemplateBadge = styled.span`
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
`;

const LeadCount = styled.span`
  background: #dbeafe;
  color: #1d4ed8;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const PageActions = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          border: none;
          &:hover {
            background: #2563eb;
          }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          border: none;
          &:hover {
            background: #dc2626;
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
          }
        `;
    }
  }}
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #ef4444;
  text-align: center;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #6b7280;
  text-align: center;

  h3 {
    margin: 0 0 8px 0;
    color: #374151;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.disabled ? `
    opacity: 0.5;
    cursor: not-allowed;
  ` : `
    &:hover {
      background: #f9fafb;
    }
  `}
`;

const PaginationInfo = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

interface PagesListProps {
  onCreatePage?: () => void;
  onEditPage?: (page: LandingPage) => void;
}

export const PagesList: React.FC<PagesListProps> = ({ onCreatePage, onEditPage }) => {
  const [search, setSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [previewPage, setPreviewPage] = useState<LandingPage | null>(null);
  const [hoverPreview, setHoverPreview] = useState<{
    page: LandingPage;
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [showCustomDomainModal, setShowCustomDomainModal] = useState(false);
  const [analyticsPage, setAnalyticsPage] = useState<LandingPage | null>(null);
  
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';

  // Helper function to check if error is onboarding related
  const isOnboardingError = (error: any) => {
    if (!error) return false;
    if ('data' in error && error.data && typeof error.data === 'object') {
      // Check both error.data.message and error.data.error for the onboarding message
      const errorMessage = error.data.message || error.data.error || '';
      return errorMessage.includes('Customer account setup required') ||
             errorMessage.includes('complete onboarding');
    }
    return false;
  };

  const { data, isLoading, error } = useGetPagesQuery({
    page,
    limit: 12,
    search: search || undefined,
    template: templateFilter || undefined,
    isActive: statusFilter ? statusFilter === 'active' : undefined,
    customerId: !isAdmin ? user?.customerId || undefined : undefined,
  });

  const [deletePage] = useDeletePageMutation();

  const handleDelete = async (pageId: string) => {
    if (window.confirm('Are you sure you want to delete this landing page?')) {
      try {
        await deletePage(pageId).unwrap();
      } catch (error) {
        console.error('Failed to delete page:', error);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleMouseEnter = (event: React.MouseEvent, page: LandingPage) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverPreview({
      page,
      mouseX: rect.right,
      mouseY: rect.top
    });
  };

  const handleMouseLeave = () => {
    setHoverPreview(null);
  };

  const handleCustomDomainSuccess = (data: any) => {
    console.log('Custom domain associated successfully:', data);
    setShowCustomDomainModal(false);
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  };

  const getTrackEventUrl = (slug: string) => {
    return `${getApiBaseUrl()}/analytics/track/${slug}`;
  };

  const getSubmitLeadUrl = (slug: string) => {
    return `${getApiBaseUrl()}/leads/contact/${slug}`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingState>Loading landing pages...</LoadingState>
      </Container>
    );
  }

  if (error) {
    // Check if this is an onboarding error
    if (isOnboardingError(error)) {
      return (
        <Container>
          <Header>
            <Title>Landing Pages</Title>
          </Header>
          <OnboardingTrigger
            icon="🚀"
            title="Complete Your Account Setup"
            description="You need to complete your account setup before you can access landing pages. This will only take a few minutes."
            buttonText="Complete Setup"
          />
        </Container>
      );
    }

    return (
      <Container>
        <ErrorState>Failed to load landing pages</ErrorState>
      </Container>
    );
  }

  const pages = data?.pages || [];
  const pagination = data?.pagination;

  // Check if user needs onboarding
  const needsOnboarding = user?.role === 'customer' && (!user?.customerId || user?.customerId === null);
  const hasNoData = pages.length === 0 && !search && !templateFilter;


  // Show onboarding for customers without customerId when they have no pages and no active filters
  if (needsOnboarding && hasNoData) {
    return (
      <Container>
        <Header>
          <Title>Landing Pages</Title>
        </Header>
        <OnboardingTrigger
          icon="🚀"
          title="Create Your First Landing Page!"
          description="Set up your account and build beautiful landing pages to showcase your business and capture leads."
          buttonText="Set Up My Account"
        />
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Landing Pages</Title>
        <Controls>
          <SearchInput
            type="text"
            placeholder="Search pages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FilterSelect
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
          >
            <option value="">All Templates</option>
            <option value="modern">Modern</option>
            <option value="startup">Startup</option>
            <option value="agency">Agency</option>
            <option value="ecommerce">E-commerce</option>
          </FilterSelect>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </FilterSelect>
          {user?.role === 'customer' && (
            <SecondaryButton onClick={() => setShowCustomDomainModal(true)}>
              Associate Domain
            </SecondaryButton>
          )}
          <CreateButton onClick={onCreatePage}>
            Create Page
          </CreateButton>
        </Controls>
      </Header>

      {pages.length === 0 ? (
        <EmptyState>
          <h3>No landing pages found</h3>
          <p>
            {search || templateFilter || statusFilter 
              ? 'Try adjusting your filters' 
              : isAdmin 
                ? 'Create your first landing page to get started'
                : 'No landing pages have been created yet'
            }
          </p>
        </EmptyState>
      ) : (
        <PagesGrid>
          {pages.map((page) => (
            <PageCard 
              key={page.id}
              onMouseEnter={(e) => handleMouseEnter(e, page)}
              onMouseLeave={handleMouseLeave}
            >
              <PageHeader>
                <PageTitle>{page.title}</PageTitle>
                <StatusBadge isActive={page.isActive}>
                  {page.isActive ? 'Active' : 'Inactive'}
                </StatusBadge>
              </PageHeader>

              <PageMeta>
                <MetaRow>
                  <MetaLabel>Template:</MetaLabel>
                  <TemplateBadge>{page.template}</TemplateBadge>
                </MetaRow>
                <MetaRow>
                  <MetaLabel>Leads:</MetaLabel>
                  <LeadCount>{page.leadCount}</LeadCount>
                </MetaRow>
                <MetaRow>
                  <MetaLabel>URL:</MetaLabel>
                  <MetaValue>{page.url}</MetaValue>
                </MetaRow>
                {page.customDomain && (
                  <MetaRow>
                    <MetaLabel>Custom Domain:</MetaLabel>
                    <MetaValue>{page.customDomain}</MetaValue>
                  </MetaRow>
                )}
                {isAdmin && (
                  <MetaRow>
                    <MetaLabel>Customer:</MetaLabel>
                    <MetaValue>{page.customer.name}</MetaValue>
                  </MetaRow>
                )}
                <MetaRow>
                  <MetaLabel>Created:</MetaLabel>
                  <MetaValue>{formatDate(page.createdAt)}</MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaLabel>Track Event:</MetaLabel>
                  <LinkContainer>
                    <LinkValue>{getTrackEventUrl(page.slug)}</LinkValue>
                    <CopyLinkButton onClick={() => copyToClipboard(getTrackEventUrl(page.slug))}>
                      Copy
                    </CopyLinkButton>
                  </LinkContainer>
                </MetaRow>
                <MetaRow>
                  <MetaLabel>Submit Lead:</MetaLabel>
                  <LinkContainer>
                    <LinkValue>{getSubmitLeadUrl(page.slug)}</LinkValue>
                    <CopyLinkButton onClick={() => copyToClipboard(getSubmitLeadUrl(page.slug))}>
                      Copy
                    </CopyLinkButton>
                  </LinkContainer>
                </MetaRow>
              </PageMeta>

              <PageActions>
                <ActionButton onClick={() => onEditPage?.(page)}>
                  Edit
                </ActionButton>
                <ActionButton 
                  onClick={() => setPreviewPage(page)}
                >
                  Preview
                </ActionButton>
                <ActionButton 
                  onClick={() => setAnalyticsPage(page)}
                >
                  Analytics
                </ActionButton>
                {isAdmin && (
                  <ActionButton 
                    variant="danger"
                    onClick={() => handleDelete(page.id)}
                  >
                    Delete
                  </ActionButton>
                )}
              </PageActions>
            </PageCard>
          ))}
        </PagesGrid>
      )}

      {pagination && pagination.totalPages > 1 && (
        <Pagination>
          <PaginationButton
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </PaginationButton>
          <PaginationInfo>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </PaginationInfo>
          <PaginationButton
            disabled={page >= pagination.totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </PaginationButton>
        </Pagination>
      )}

      {previewPage && (
        <PagePreview 
          page={previewPage} 
          onClose={() => setPreviewPage(null)} 
        />
      )}

      {hoverPreview && (
        <HoverPreview
          page={hoverPreview.page}
          show={!!hoverPreview}
          mouseX={hoverPreview.mouseX}
          mouseY={hoverPreview.mouseY}
        />
      )}

      <CustomDomainModal
        isOpen={showCustomDomainModal}
        onClose={() => setShowCustomDomainModal(false)}
        onSuccess={handleCustomDomainSuccess}
      />

      {analyticsPage && (
        <AnalyticsModal
          isOpen={!!analyticsPage}
          page={analyticsPage}
          onClose={() => setAnalyticsPage(null)}
        />
      )}
    </Container>
  );
};